import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { sessionService } from '../../../application/SessionService';
import { EventTrackTypes } from '../../../constants/EventTracks';
import { useCaptureEvents } from '../../../hooks/useCaptureEvents';
import { useShowSnackbarMessage } from '../../../hooks/useShowSnackbarMessage';
import { LoginProvider } from '../../../repositories/session/SessionRepository';
import { useAuth } from './useAuth';

interface UseSessionLoginProviderProps {
  provider: LoginProvider;
  providerToken: string;
  accessToken: string;
  userName?: string;
}

type UseSessionLoginProviderResponseProps = ServiceResponse<
  ({ provider, providerToken, accessToken, userName }: UseSessionLoginProviderProps) => void,
  void
>;

// Hooks act as 'Adapter' layer.
export const useSessionLoginProvider = (): UseSessionLoginProviderResponseProps => {
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const queryClient = useQueryClient();
  const { registerData, setAuthScreen, setContextData: setRegisterData } = useAuth();
  const { capture } = useCaptureEvents();

  const { isLoading, error, isSuccess, mutate } = useMutation(
    ({ provider, providerToken, accessToken }: UseSessionLoginProviderProps) =>
      sessionService.loginProvider({ provider, providerToken, accessToken }),
    {
      onSuccess: (_, { provider }) => {
        capture({
          type: EventTrackTypes.UserLoggedIn,
          payload: {
            [`From`]: provider === `apple` ? `Apple` : `Google`,
          },
        });
        queryClient.invalidateQueries([`user_is_logged`]);
      },
      onError: (err: any, variables: UseSessionLoginProviderProps) => {
        const { provider, providerToken, accessToken, userName } = variables;
        if (err.response.status === 400 && err.response.data.messageId === `highway.vehicle.error.email_not_verified`) {
          showSnackbarMessage({
            messageIntlId: `infoerror.drivers.login.error.email_not_verified.${provider}`,
          });
          return;
        }

        // Only if error is "vehicle_not_found", moves to register screen.
        if (err.response.status === 403 && err.response.data.messageId === `highway.vehicle.error.not_found`) {
          setAuthScreen(`register-data`);
          setRegisterData({ ...registerData, providerToken, accessToken, provider, organizationName: userName });
          return;
        }

        console.info(`Error on session login provider process`, err, err.response.status);
        showSnackbarMessage({
          messageIntlId: `infoerror.drivers.login.error`,
        });
      },
    }
  );

  return {
    error: error,
    success: isSuccess,
    isLoading: isLoading,
    run: mutate,
  };
};
